// lib/api-client.ts — single base URL for the frontend (VITE_API_URL),
// shared with every backend data route. One origin, one cookie scope, one
// CORS table.
//
// All routes are guarded by X-API-Key on the backend (see apps/backend/src/
// utils/auth.ts). The API_KEYS env var in the backend validates that header.
// We pass it on every request so the same frontend build works against any
// environment that has the matching key configured server-side.

/** Same-origin path nginx reverse-proxies to the backend in prod. */
const PROXY_BASE_URL = "/api";
/** Direct browser->backend origin, for `vite dev` with no VITE_API_URL set. */
const DEV_BASE_URL = "http://localhost:3010";

// VITE_API_URL is inlined at BUILD time. A blank or malformed value used to
// reach `new URL(path, "")` and throw "Failed to construct 'URL': Invalid base
// URL" — an unhandled TypeError that broke every page, with the real cause
// (a misconfigured build arg) invisible from the UI. Note `??` is not enough:
// it only catches null/undefined, and an empty env var inlines as "".
function resolveBaseUrl(raw: string | undefined): string {
  // Absent entirely => `vite dev` without a .env. Preserve dev behaviour.
  if (raw === undefined) return DEV_BASE_URL;

  const value = raw.trim();
  if (!value) {
    console.error(
      `[api-client] VITE_API_URL is blank; falling back to "${PROXY_BASE_URL}". ` +
        'Set it to a same-origin path like "/api" or an absolute origin.',
    );
    return PROXY_BASE_URL;
  }
  // Same-origin prefix ("/api"). buildUrl() resolves it against the page origin.
  if (value.startsWith("/")) return value;
  // Otherwise it must be a valid absolute URL.
  try {
    new URL(value);
    return value;
  } catch {
    console.error(
      `[api-client] VITE_API_URL=${JSON.stringify(raw)} is not a valid absolute ` +
        `URL or "/"-prefixed path; falling back to "${PROXY_BASE_URL}".`,
    );
    return PROXY_BASE_URL;
  }
}

const BASE_URL = resolveBaseUrl(import.meta.env.VITE_API_URL);
const API_KEY = (import.meta.env.VITE_API_KEY ?? "").trim();

if (!API_KEY) {
  console.error(
    "[api-client] VITE_API_KEY is empty, so no X-API-Key header will be sent. " +
      "Every data route will return 401. Set it at build time to a value " +
      "present in the backend's API_KEYS.",
  );
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

type RequestOpts = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  query?: Record<string, string | number | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the X-API-Key header (only for /health). */
  skipApiKey?: boolean;
  /** Clerk session JWT. Send on routes whose backend handler runs clerkAuthGuard
   *  (companies PUT, preferences GET/PUT, ai-analyses POST, document-metadata PUT);
   *  the backend ignores it everywhere else. Omit for read-only GETs — keeps the
   *  bundle from bundling Clerk's token getter into every page. */
  token?: string;
  /** Extra request headers. v2 cache-bust via `X-No-Cache: 1` lives here. */
  headers?: Record<string, string>;
};

function buildUrl(path: string, query?: RequestOpts["query"]): string {
  // VITE_API_URL can be either an absolute origin (dev — direct browser->backend
  // call) or a relative base like "/api" (prod — nginx reverse-proxies /api/* to
  // the backend on the same origin). For the relative case we can't use
  // `new URL(path, "/api")` because URL would treat the base as the root and
  // drop the prefix. Manual concat instead.
  const url = BASE_URL.startsWith("/")
    ? new URL(`${BASE_URL.replace(/\/$/, "")}${path}`, window.location.origin)
    : new URL(path, BASE_URL);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (!opts.skipApiKey && API_KEY) {
    headers["X-API-Key"] = API_KEY;
  }
  if (opts.token) {
    headers.Authorization = `Bearer ${opts.token}`;
  }
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) {
      headers[k] = v;
    }
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    credentials: "include",
  });

  const text = await res.text();
  const payload = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    const message =
      (payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : null) ?? `Request failed with status ${res.status}`;
    throw new ApiError(message, res.status, payload);
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const apiBase = BASE_URL;
